import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * Fuel Savings Opportunity — read-only dashboard for fleet owners / dispatch managers.
 * Reads GET /scripted/fleet/fuel-savings-dashboard (fuel_savings_dashboard.build_dashboard).
 * Strictly separates the four financial tiers and the two recommendation bases; explains missing
 * evidence; never presents a positive price gap as realized/causal savings.
 */
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });

const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const usd2 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
const gal = (v) => (v == null ? "—" : `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} gal`);

const EVIDENCE_LABELS = {
  unresolved_truck_identity: "Unresolved truck identity",
  no_fuel_recommendation: "No fuel recommendation on the load",
  no_captured_sms: "No captured SMS send",
  delivery_unverified_or_unknown: "Delivery accepted but not confirmed",
  unverified_station_snapshot: "Unverified station in sent plan",
  timezone_uncertainty: "Timezone not yet validated",
  incomparable_or_stale_prices: "Incomparable / stale prices",
  other_insufficient: "Other insufficient evidence",
};

function Kpi({ label, value, sub, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900 dark:text-slate-100",
    teal: "text-teal-700 dark:text-teal-300",
    amber: "text-amber-700 dark:text-amber-400",
    muted: "text-slate-400",
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

export default function FuelSavingsOpportunity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("dispatcher");
  const [filters, setFilters] = useState({ date_from: "", date_to: "", dispatcher_id: "", truck_id: "", driver_id: "", basis: "" });

  useEffect(() => {
    let live = true;
    setLoading(true); setErr(null);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    axios.get(`${apiBaseUrl}/fleet/fuel-savings-dashboard`, { params, ...authHeaders() })
      .then((r) => { if (live) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (live) { setErr(e?.response?.data?.error || e.message); setLoading(false); } });
    return () => { live = false; };
  }, [filters]);

  const ov = data?.overview || {};
  const sent = data?.sent_plan_adherence || {};
  const basis = data?.by_basis || {};
  const rollup = useMemo(() => (data ? (data[`by_${tab}`] || []) : []), [data, tab]);
  const noSentData = !sent.loads;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 text-slate-800 dark:text-slate-100">
      <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">AIVAN · Fuel program</div>
      <h1 className="text-2xl font-bold tracking-tight">Fuel Savings Opportunity</h1>
      <p className="text-slate-500 text-sm mt-1 max-w-3xl">
        Receipt-verified diesel spend, how closely drivers followed the fuel plan they were actually sent,
        and the defensible opportunity left on the table. Observed differences are descriptive —
        <span className="font-medium"> not realized or causal savings</span>.
      </p>

      {/* filters */}
      <div className="mt-4 flex flex-wrap gap-2 items-end">
        <Field label="From"><input type="date" className="inp" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></Field>
        <Field label="To"><input type="date" className="inp" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></Field>
        <Field label="Dispatcher"><input className="inp" placeholder="id" value={filters.dispatcher_id} onChange={(e) => setFilters({ ...filters, dispatcher_id: e.target.value })} /></Field>
        <Field label="Truck"><input className="inp" placeholder="id" value={filters.truck_id} onChange={(e) => setFilters({ ...filters, truck_id: e.target.value })} /></Field>
        <Field label="Driver"><input className="inp" placeholder="id" value={filters.driver_id} onChange={(e) => setFilters({ ...filters, driver_id: e.target.value })} /></Field>
        <Field label="Basis">
          <select className="inp" value={filters.basis} onChange={(e) => setFilters({ ...filters, basis: e.target.value })}>
            <option value="">All</option>
            <option value="sent_plan">Sent plan (verified)</option>
            <option value="live_plan_modeled">Live plan (modeled / historical)</option>
          </select>
        </Field>
      </div>

      {loading && <div className="mt-8 text-slate-400">Loading…</div>}
      {err && <div className="mt-8 rounded-lg border border-red-200 bg-red-50 text-red-700 p-3 text-sm">Couldn’t load the dashboard: {err}</div>}

      {data && !loading && (
        <>
          {/* overview KPIs */}
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Actual paid (fuel)" value={usd(ov.actual_paid_fuel)} sub="verified Relay card receipts (invoiced $)" tone="slate" />
            <Kpi label="Eligible purchases" value={ov.eligible_purchases ?? "—"} sub={`${ov.comparable_purchases ?? 0} comparable`} />
            <Kpi label="Verified station adherence" value={pct(ov.verified_station_adherence_rate)} sub="sent-plan stops followed" tone="teal" />
            <Kpi label="Gallon variance (followed)" value={gal(ov.gallon_variance_total)} sub="purchased vs recommended" />
            <Kpi label="Observed price difference" value={usd2(ov.observed_price_difference_modeled)} sub="descriptive · modeled basis" tone="amber" />
            <Kpi label="Modeled avoidable" value={usd2(ov.modeled_avoidable)} sub="defensible, modeled basis" tone="amber" />
            <Kpi label="Demonstrated incremental savings" value={ov.demonstrated_status || "Not established"} sub={ov.demonstrated_note} tone="muted" />
          </div>

          {/* basis separation */}
          <Section title="Recommendation basis (kept separate)">
            <div className="grid md:grid-cols-2 gap-3">
              <BasisCard title="Sent plan — verified adherence" b={basis.sent_plan} accent="teal"
                note="From loads where a fuel plan was actually sent and its stations verified." empty={!basis.sent_plan?.eligible_purchases} />
              <BasisCard title="Live plan — modeled / historical" b={basis.live_plan_modeled} accent="amber"
                note="Modeled opportunity vs the live plan; NOT evidence a recommendation was sent." empty={!basis.live_plan_modeled?.eligible_purchases} />
            </div>
          </Section>

          {/* sent-plan adherence detail */}
          <Section title="Sent-plan adherence">
            {noSentData ? (
              <Empty>
                No captured sent-plan deliveries yet. Station & gallon adherence populate once the manual
                fuel-SMS capture is enabled in production. (Historical modeled opportunity is shown above
                under “Live plan”.)
              </Empty>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label="Loads with a sent plan" value={sent.loads} />
                <Kpi label="Stops followed" value={`${sent.stations_followed}/${sent.stations_recommended}`} tone="teal" />
                <Kpi label="Deviations" value={sent.deviations} tone="amber" />
                <Kpi label="Gallon variance" value={gal(sent.gallon_variance_total)} />
                <Kpi label="Defensible avoidable" value={usd2(sent.defensible_avoidable)} sub="deviations only" tone="amber" />
              </div>
            )}
          </Section>

          {/* rollups */}
          <Section title="Breakdown" right={
            <div className="flex gap-1 text-xs">
              {["dispatcher", "truck", "driver"].map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-2.5 py-1 rounded-md capitalize ${tab === t ? "bg-teal-600 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"}`}>{t}</button>
              ))}
            </div>
          }>
            {rollup.length === 0 ? <Empty>No {tab} rows for these filters.</Empty> : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase text-slate-500">
                    <tr><th className="text-left p-2">{tab}</th><th className="text-right p-2">Purchases</th>
                      <th className="text-right p-2">Actual spend</th><th className="text-right p-2">Observed diff</th>
                      <th className="text-right p-2">Modeled avoidable</th></tr>
                  </thead>
                  <tbody>
                    {rollup.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                        <td className="p-2">{r.dispatcher || r.truck_unit || r.driver_id}</td>
                        <td className="p-2 text-right tabular-nums">{r.eligible_purchases}</td>
                        <td className="p-2 text-right tabular-nums">{usd(r.actual_relay_spend)}</td>
                        <td className="p-2 text-right tabular-nums">{usd2(r.observed_price_difference)}</td>
                        <td className="p-2 text-right tabular-nums text-amber-700 dark:text-amber-400">{usd2(r.modeled_avoidable)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* evidence breakdown */}
          <Section title="Why some purchases can’t be scored (evidence gaps)">
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.evidence_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1 text-xs">
                  <span className="font-semibold tabular-nums">{v}</span>
                  <span className="text-slate-500">{EVIDENCE_LABELS[k] || k}</span>
                </span>
              ))}
              {Object.keys(data.evidence_breakdown || {}).length === 0 && <Empty>No evidence gaps in range.</Empty>}
            </div>
          </Section>

          {/* purchase drill-down */}
          <Section title="Purchases">
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2">Truck</th><th className="text-left p-2">Basis</th>
                    <th className="text-left p-2">Actual station</th><th className="text-left p-2">Recommended</th>
                    <th className="text-right p-2">Gal</th><th className="text-right p-2">$/gal</th>
                    <th className="text-right p-2">Gap/gal</th><th className="text-left p-2">Delivery</th>
                    <th className="text-left p-2">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.purchases || []).map((p) => (
                    <tr key={p.line_item_id} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="p-2">{p.truck_unit || "—"}</td>
                      <td className="p-2"><Badge basis={p.basis} /></td>
                      <td className="p-2">{p.actual_station || "—"}</td>
                      <td className="p-2">{p.recommended_station || "—"}</td>
                      <td className="p-2 text-right tabular-nums">{p.actual_gallons ?? "—"}</td>
                      <td className="p-2 text-right tabular-nums">{p.actual_net_ppg != null ? `$${Number(p.actual_net_ppg).toFixed(3)}` : "—"}</td>
                      <td className="p-2 text-right tabular-nums">{p.price_gap_per_gal != null ? `$${Number(p.price_gap_per_gal).toFixed(3)}` : "—"}</td>
                      <td className="p-2 text-xs">{p.delivery_status || <span className="text-slate-400">no SMS</span>}</td>
                      <td className="p-2 text-xs text-slate-500">{p.evidence_class}</td>
                    </tr>
                  ))}
                  {(data.purchases || []).length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400">No purchases for these filters.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Delivery states: <b>accepted</b> = provider took it (not proof of receipt) · <b>delivered</b> = confirmed ·
              <b> failed</b> / <b>unknown</b>. Adherence counts only verified sent-plan stops.
            </p>
          </Section>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="flex flex-col text-xs text-slate-500 gap-1">{label}{children}</label>;
}
function Empty({ children }) {
  return <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-5 text-sm text-slate-500 bg-slate-50/50 dark:bg-slate-800/40">{children}</div>;
}
function Badge({ basis }) {
  const sent = basis === "sent_plan";
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${sent ? "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200" : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"}`}>{sent ? "sent" : "modeled"}</span>;
}
function BasisCard({ title, b, note, accent, empty }) {
  b = b || {};
  return (
    <div className={`rounded-xl border p-4 ${accent === "teal" ? "border-teal-200 dark:border-teal-800" : "border-amber-200 dark:border-amber-800"} bg-white dark:bg-slate-800`}>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-slate-400 mt-0.5">{note}</div>
      {empty ? <div className="mt-3 text-sm text-slate-400">No data yet.</div> : (
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div><div className="text-xs text-slate-400">Purchases</div><div className="tabular-nums">{b.eligible_purchases} <span className="text-slate-400">({b.comparable_purchases} comp.)</span></div></div>
          <div><div className="text-xs text-slate-400">Actual spend</div><div className="tabular-nums">{usd(b.actual_relay_spend)}</div></div>
          <div><div className="text-xs text-slate-400">Observed diff</div><div className="tabular-nums">{usd2(b.observed_price_difference)}</div></div>
          <div><div className="text-xs text-slate-400">Modeled avoidable</div><div className="tabular-nums text-amber-700 dark:text-amber-400">{usd2(b.modeled_avoidable)}</div></div>
        </div>
      )}
    </div>
  );
}
